import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  AuthProvider,
  CompanyProvider,
  startLineLogin,
  handleLineLoginCallback,
  getLineAuthErrorMessage,
  getCompanies,
  configureDataslotApi
} from "@x-veloper/dataslot-auth";
import App from "./App";
import "./index.css";

// Configure module for MasterLabel
const MODULE = "MLOper";

// Configure Dataslot API base URL from environment variable
const dataslotApiBaseUrl = import.meta.env.VITE_DATASLOT_HTTPAPI_BASE_URL || 'https://dev.open-api.dataslot.app';
configureDataslotApi(dataslotApiBaseUrl);
console.log('[App] Configured Dataslot API base URL:', dataslotApiBaseUrl);

// Firebase configuration
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
};

// LINE auth handlers for the AuthProvider
const lineAuthHandlers = {
  signInWithLine: () => startLineLogin(),
  handleLineCallback: async (url: string) => {
    await handleLineLoginCallback(url);
  },
  getLineAuthError: (error: any) => getLineAuthErrorMessage(error)
};

// Company service configured with MLOper module
const getCompaniesService = () => getCompanies(MODULE);

// Create React Query client with optimized settings for analytics data
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - analytics data doesn't change frequently
      gcTime: 10 * 60 * 1000, // 10 minutes - keep cached data longer
      retry: 1, // Retry failed queries once
      refetchOnWindowFocus: false, // Don't refetch on window focus for analytics
    },
  },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider firebaseConfig={firebaseConfig} lineAuthHandlers={lineAuthHandlers}>
          <CompanyProvider getCompaniesService={getCompaniesService}>
            <App />
          </CompanyProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
);